import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledJsonInput from '@/components/StyledInputFields/StyledJsonInput.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import useUpdateAutomationVersionSchemas from '@/hooks/api/automationVersions/useUpdateAutomationVersionSchemas.ts';
import {
  AutomationJsonSchema,
  AutomationSchemaRef,
  SchemaValidationMode,
} from '@/types/automationFields.ts';
import { STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import { ActionIcon, Accordion, Badge, Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconCode, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

type Props = { schemas: AutomationJsonSchema[] };
type SchemaFormState = {
  schema_id: string;
  version: number;
  description: string;
  validation_mode: SchemaValidationMode;
  json_schema_text: string;
};

const EMPTY_JSON_SCHEMA = { type: 'object', properties: {}, additionalProperties: true };
const VALIDATION_MODE_OPTIONS = [
  { label: 'Error', value: SchemaValidationMode.Error },
  { label: 'Warn', value: SchemaValidationMode.Warn },
];
const validationModeDetails: Record<SchemaValidationMode, { label: string; description: string }> =
  {
    [SchemaValidationMode.Error]: {
      label: 'Block on mismatch',
      description: 'A mismatch fails the run.',
    },
    [SchemaValidationMode.Warn]: {
      label: 'Warn on mismatch',
      description: 'A mismatch is recorded as a warning.',
    },
  };
const schemaRefMatches = (left?: AutomationSchemaRef | null, right?: AutomationSchemaRef | null) =>
  left?.schema_id === right?.schema_id && left?.version === right?.version;
const schemaToRef = (schema: AutomationJsonSchema): AutomationSchemaRef => ({
  schema_id: schema.schema_id,
  version: schema.version,
});
const schemaLabel = (schema: AutomationJsonSchema) => `${schema.schema_id} v${schema.version}`;
const familyLabel = (schemaId: string) => {
  const words = schemaId
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : 'Unnamed schema';
};
const createState = (): SchemaFormState => ({
  schema_id: '',
  version: 1,
  description: '',
  validation_mode: SchemaValidationMode.Error,
  json_schema_text: JSON.stringify(EMPTY_JSON_SCHEMA, null, 2),
});
const editState = (schema: AutomationJsonSchema): SchemaFormState => ({
  schema_id: schema.schema_id,
  version: schema.version,
  description: schema.description,
  validation_mode: schema.validation_mode,
  json_schema_text: JSON.stringify(schema.json_schema, null, 2),
});
const parseSchema = (value: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const AutomationSchemasPanel = ({ schemas }: Props) => {
  const { mutate: updateSchemas, isPending } = useUpdateAutomationVersionSchemas();
  const [editingSchema, setEditingSchema] = useState<AutomationJsonSchema | null>(null);
  const [schemaToDelete, setSchemaToDelete] = useState<AutomationJsonSchema | null>(null);
  const [viewingSchema, setViewingSchema] = useState<AutomationJsonSchema | null>(null);
  const [formState, setFormState] = useState<SchemaFormState | null>(null);
  const parsedSchema = formState ? parseSchema(formState.json_schema_text) : null;
  const trimmedSchemaId = formState?.schema_id.trim() ?? '';
  const duplicate = schemas.some(
    (schema) =>
      !(editingSchema && schemaRefMatches(schemaToRef(schema), schemaToRef(editingSchema))) &&
      schema.schema_id === trimmedSchemaId &&
      schema.version === formState?.version,
  );
  const formError =
    formState &&
    (!trimmedSchemaId
      ? 'Schema ID is required'
      : formState.version < 1
        ? 'Version must be at least 1'
        : duplicate
          ? 'Schema ID and version already exist'
          : !parsedSchema
            ? 'JSON Schema must be an object'
            : null);
  const families = Array.from(
    schemas.reduce((result, schema) => {
      result.set(schema.schema_id, [...(result.get(schema.schema_id) ?? []), schema]);
      return result;
    }, new Map<string, AutomationJsonSchema[]>()),
  ).sort(([left], [right]) => left.localeCompare(right));

  const closeForm = () => {
    setEditingSchema(null);
    setFormState(null);
  };
  const saveSchema = () => {
    if (!formState || !parsedSchema || formError) return;
    const next: AutomationJsonSchema = {
      schema_id: trimmedSchemaId,
      version: formState.version,
      description: formState.description,
      validation_mode: formState.validation_mode,
      json_schema: parsedSchema,
    };
    updateSchemas({
      updater: (current) =>
        editingSchema
          ? current.map((schema) =>
              schemaRefMatches(schemaToRef(schema), schemaToRef(editingSchema)) ? next : schema,
            )
          : [...current, next],
    });
    closeForm();
  };
  const deleteSchema = () => {
    if (!schemaToDelete) return;
    updateSchemas({
      updater: (current) =>
        current.filter(
          (schema) => !schemaRefMatches(schemaToRef(schema), schemaToRef(schemaToDelete)),
        ),
    });
    setSchemaToDelete(null);
  };

  return (
    <>
      <Stack gap="xs">
        <Text size="xs" c="dimmed">
          Schemas define the expected shape of data passed between automation steps.
        </Text>
        {families.length === 0 ? (
          <Text size="12px" c="dimmed">
            No schemas
          </Text>
        ) : (
          <Accordion variant="contained">
            {families.map(([schemaId, versions]) => (
              <Accordion.Item key={schemaId} value={schemaId}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap" pr="sm">
                    <Text fw={500} size="sm" truncate>
                      {familyLabel(schemaId)}
                    </Text>
                    <Group gap={4} wrap="nowrap">
                      {versions
                        .sort((a, b) => b.version - a.version)
                        .map((schema) => (
                          <Badge key={schema.version} color="gray" variant="light" size="xs">
                            v{schema.version}
                          </Badge>
                        ))}
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {versions
                      .sort((a, b) => b.version - a.version)
                      .map((schema) => (
                        <Group
                          key={schemaLabel(schema)}
                          justify="space-between"
                          wrap="nowrap"
                          p="xs"
                          style={{ border: '1px solid #ECECED', borderRadius: 5 }}
                        >
                          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                            <Group gap="xs">
                              <Text size="xs" fw={500}>
                                Schema v{schema.version}
                              </Text>
                              <Tooltip
                                label={validationModeDetails[schema.validation_mode].description}
                              >
                                <Badge color="gray" variant="light" size="xs">
                                  {validationModeDetails[schema.validation_mode].label}
                                </Badge>
                              </Tooltip>
                            </Group>
                            {schema.description && (
                              <Text size="xs" c="dimmed">
                                {schema.description}
                              </Text>
                            )}
                          </Stack>
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label="View schema definition">
                              <ActionIcon
                                aria-label={`View definition for ${schemaLabel(schema)}`}
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={() => setViewingSchema(schema)}
                              >
                                <IconCode size={15} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Edit schema">
                              <ActionIcon
                                aria-label={`Edit ${schemaLabel(schema)}`}
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={() => {
                                  setEditingSchema(schema);
                                  setFormState(editState(schema));
                                }}
                              >
                                <IconEdit size={15} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete schema">
                              <ActionIcon
                                aria-label={`Delete ${schemaLabel(schema)}`}
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={() => setSchemaToDelete(schema)}
                              >
                                <IconTrash size={15} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
        <StyledButton
          variant="outline"
          size="md"
          onClick={() => {
            setEditingSchema(null);
            setFormState(createState());
          }}
          leftSection={<IconPlus size={14} />}
          loading={isPending}
        >
          Add schema
        </StyledButton>
      </Stack>
      <StyledModal
        opened={viewingSchema !== null}
        onClose={() => setViewingSchema(null)}
        title={
          viewingSchema
            ? `${familyLabel(viewingSchema.schema_id)} v${viewingSchema.version}`
            : 'Schema definition'
        }
        scrollType="body"
        size="lg"
      >
        {viewingSchema && (
          <Box
            component="pre"
            p="xs"
            style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: 6,
              fontSize: 11,
              lineHeight: 1.45,
              margin: 0,
              overflowX: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {JSON.stringify(viewingSchema.json_schema, null, 2)}
          </Box>
        )}
      </StyledModal>
      <StyledModal
        opened={formState !== null}
        onClose={closeForm}
        title={editingSchema ? 'Edit Schema' : 'Add Schema'}
        scrollType="body"
        size="lg"
      >
        {formState && (
          <Stack gap="xs">
            <StyledTextInput
              label="Schema ID"
              value={formState.schema_id}
              onChange={(event) =>
                setFormState({ ...formState, schema_id: event.currentTarget.value })
              }
              disabled={!!editingSchema}
              maxLength={128}
              required
            />
            <StyledTextInput
              label="Version"
              type="number"
              value={formState.version}
              onChange={(event) =>
                setFormState({ ...formState, version: Number(event.currentTarget.value || 1) })
              }
              disabled={!!editingSchema}
              min={1}
              required
            />
            <StyledSelect
              label="Validation Mode"
              data={VALIDATION_MODE_OPTIONS}
              value={formState.validation_mode}
              onChange={(value) =>
                setFormState({
                  ...formState,
                  validation_mode: (value as SchemaValidationMode) || SchemaValidationMode.Error,
                })
              }
              allowDeselect={false}
              clearable={false}
            />
            <StyledTextarea
              label="Description"
              value={formState.description}
              onChange={(event) =>
                setFormState({ ...formState, description: event.currentTarget.value })
              }
              autosize
              minRows={2}
              maxRows={5}
              maxLength={STEP_FIELD_MAX_LENGTH}
            />
            <StyledJsonInput
              label="JSON Schema"
              value={formState.json_schema_text}
              onChange={(value) => setFormState({ ...formState, json_schema_text: value })}
              autosize
              minRows={8}
              maxRows={20}
              validationError="Invalid JSON"
              formatOnBlur
              required
            />
            {formError && (
              <Text size="12px" c="red">
                {formError}
              </Text>
            )}
            <ModalConfirmationButtons
              onClose={closeForm}
              onConfirm={saveSchema}
              confirmText="Save"
              closeOnConfirm={false}
              confirmDisabled={!!formError}
              confirmLoading={isPending}
            />
          </Stack>
        )}
      </StyledModal>
      <ConfirmationModal
        opened={schemaToDelete !== null}
        onClose={() => setSchemaToDelete(null)}
        title="Delete Schema"
        message={
          schemaToDelete
            ? `Delete ${schemaLabel(schemaToDelete)}? Existing references will be reported by validation.`
            : ''
        }
        onConfirm={deleteSchema}
        confirmText="Delete"
        confirmType="delete"
      />
    </>
  );
};

export default AutomationSchemasPanel;
